import React, { useMemo } from 'react';
import { Row, Col, Form, InputGroup } from 'react-bootstrap';
import { ensureWeekendExtras } from './weekendExtras';
import { WEEKEND_EXTRAS, buildExtraOwnershipIndex } from './extraOwnership';

const normalizeAmount = value => {
  const number = Number(value === '' ? 0 : value);
  return Number.isFinite(number) && number >= 0
    ? Math.round(number * 100) / 100
    : 0;
};

export default function WeekendExtrasSection({
  formik,
  extrasCatalog,
  currencyCode = 'EUR',
}) {
  const ownershipIndex = useMemo(
    () => buildExtraOwnershipIndex(extrasCatalog),
    [extrasCatalog]
  );
  const rows = useMemo(
    () => ensureWeekendExtras(formik.values.extras, extrasCatalog, ownershipIndex),
    [formik.values.extras, extrasCatalog, ownershipIndex]
  );
  const rowByKey = useMemo(
    () => new Map(rows.map(row => [row.key, row])),
    [rows]
  );
  const currencySymbol = useMemo(() => {
    if (currencyCode === 'EUR') return '€';
    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
      }).formatToParts(0).find(part => part.type === 'currency')?.value ?? currencyCode;
    } catch {
      return currencyCode;
    }
  }, [currencyCode]);

  const patchAmount = (key, value) => {
    formik.setValues(previous => ({
      ...previous,
      extras: ensureWeekendExtras(previous.extras, extrasCatalog, ownershipIndex)
        .map(row => row.key === key ? { ...row, pricePerDay: value } : row),
    }), false);
  };

  const descriptionId = 'weekend-surcharge-description';

  return (
    <Col xs={12} className="mt-4 mb-3">
      <div className="section-title">Weekend surcharges</div>
      <p id={descriptionId} className="section-desc mb-3">
        Charged once when pick-up or drop-off falls on a weekend. Enter 0 for no surcharge.
      </p>
      {['Saturday', 'Sunday'].map(day => (
        <Row key={day} className="align-items-end">
          <Col xs={12} md={2} className="mb-3">
            <div className="d-flex align-items-center" style={{ minHeight: 38 }}>
              {day}
            </div>
          </Col>
          {WEEKEND_EXTRAS.filter(definition => definition.day === day).map(definition => {
            const row = rowByKey.get(definition.key);
            const inputId = `weekend-fee-${definition.key}`;
            return (
              <Col xs={6} md={5} key={definition.key}>
                <Form.Group controlId={inputId} className="mb-3">
                  <Form.Label>
                    {definition.action} fee
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      name={definition.key}
                      aria-label={`${day} ${definition.action.toLowerCase()} fee`}
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      aria-describedby={`${descriptionId} ${inputId}-currency`}
                      value={row?.pricePerDay ?? 0}
                      onChange={event => {
                        const value = event.target.value.replace(',', '.');
                        if (/^\d*(?:\.\d{0,2})?$/.test(value)) {
                          patchAmount(definition.key, value);
                        }
                      }}
                      onBlur={event => patchAmount(
                        definition.key,
                        normalizeAmount(event.target.value)
                      )}
                    />
                    <InputGroup.Text id={`${inputId}-currency`} className="text-muted">
                      {currencySymbol}
                    </InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            );
          })}
        </Row>
      ))}
    </Col>
  );
}
